import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { segment_thumb_style } from './styles';
import type { SegmentedControlSize } from './types';
import { motion, AnimatePresence, HTMLMotionProps } from 'framer-motion';
import debounce from 'lodash-es/debounce';

interface ThumbRect {
  left: number;
  width: number;
}

const DEFAULT_THUMBRECT: ThumbRect = { left: 0, width: 0 };

const calcThumbStyle = (target: HTMLElement | null | undefined): ThumbRect => {
  if (!target) return DEFAULT_THUMBRECT;

  return {
    left: target.offsetLeft,
    width: target.clientWidth,
  };
};

export interface SegmentedControlThumbProps extends Omit<HTMLMotionProps<'div'>, 'style'> {
  size?: SegmentedControlSize;
  containerRef: HTMLDivElement;
  checkedIndex: number;
  updatedKey: string;
  onMotionStart?: () => void;
  onMotionEnd?: () => void;
}

export const SegmentedControlThumb: React.FC<SegmentedControlThumbProps> = ({
  containerRef,
  checkedIndex,
  onMotionStart,
  onMotionEnd,
  updatedKey,
  ...props
}) => {
  const thumb_ref = useRef<HTMLDivElement>(null);
  const initial_position_set_ref = useRef<boolean>(false);
  const [prev_index, setPrevIndex] = useState<number>(checkedIndex);

  const [style, setStyle] = useState<{
    duration: number;
    initial: ThumbRect;
    animate: ThumbRect;
  }>({
    duration: 0,
    initial: DEFAULT_THUMBRECT,
    animate: DEFAULT_THUMBRECT,
  });

  const getCheckedElement = useCallback((): HTMLElement | null => {
    if (checkedIndex < 0) return null;

    const items = containerRef.querySelectorAll('.pds-segmented-control-option');
    if (items.length === 0 || checkedIndex >= items.length) return null;

    const item = items[checkedIndex];
    return (item as HTMLElement) || null;
  }, [containerRef, checkedIndex]);

  const getPrevElement = useCallback((): HTMLElement | null => {
    if (prev_index < 0) return null;

    const items = containerRef.querySelectorAll('.pds-segmented-control-option');
    if (items.length === 0 || prev_index >= items.length) return null;

    const item = items[prev_index];
    return (item as HTMLElement) || null;
  }, [containerRef, prev_index]);

  const updateThumbStyle = useCallback(
    (is_initial_mount = false) => {
      const checked_element = getCheckedElement();
      if (!checked_element) return;

      const animate = calcThumbStyle(checked_element);

      if (is_initial_mount || prev_index === checkedIndex) {
        setStyle({
          duration: 0,
          initial: animate,
          animate,
        });
        if (is_initial_mount) {
          initial_position_set_ref.current = true;
        }
      } else {
        const prev_element = getPrevElement();
        const prev_element_style = prev_element ? calcThumbStyle(prev_element) : animate;

        setStyle({
          duration: 0.2,
          initial: prev_element_style,
          animate,
        });

        setPrevIndex(checkedIndex);
      }
    },
    [getCheckedElement, getPrevElement, prev_index, checkedIndex],
  );

  useLayoutEffect(() => {
    updateThumbStyle();
  }, [checkedIndex, updatedKey, updateThumbStyle]);

  useLayoutEffect(() => {
    const handleResize = debounce(() => {
      updateThumbStyle(initial_position_set_ref.current === false);
    }, 250);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateThumbStyle, initial_position_set_ref]);

  return (
    <AnimatePresence>
      <motion.div
        ref={thumb_ref}
        className='pds-segmented-control-thumb'
        role='none'
        css={segment_thumb_style}
        initial={style.initial}
        animate={style.animate}
        transition={{
          duration: style.duration,
          ease: 'easeInOut',
        }}
        onAnimationStart={() => {
          onMotionStart?.();
        }}
        onAnimationComplete={() => {
          onMotionEnd?.();
        }}
        {...props}
      />
    </AnimatePresence>
  );
};
